import React, { useEffect, useState, useRef } from "react";
import { ICellEditorParams } from "ag-grid-community";
import { Select, Spin } from "antd";
import { getActiveModule } from "@/ts/utils";
import { TagsMap, TProductMappingUpdateConfig } from "@/ts/common-types";
import { addEntityTags } from "@/ts/retrieve-data";
import { TaggingActionTypes, TAGS_MODULE_MAPPING } from "@/ts/constants";

interface VcpmGlobalTagCellEditorParams extends ICellEditorParams {
    avlTagsData: {
        tagId: string;
        tagName: string;
    }[];
}

const VcpmGlobalTagCellEditor: React.FC<VcpmGlobalTagCellEditorParams> = (
    params
) => {
    const { avlTagsData, value } = params;
    const [selectedValue, setSelectedValue] = useState<string | undefined>(
        value
    );
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Convert available tags to options format
    const options = avlTagsData.map((tag) => ({
        value: tag.tagId,
        label: tag.tagName,
    }));

    useEffect(() => {
        //to set open the select dropdown when the editor is opened
        setOpen(true);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest(".ant-select-dropdown")
            ) {
                setSelectedValue(value); // Reset to original value
                params.api.stopEditing();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [params.api, value]);
    const { activeModule } = getActiveModule();
    const { updateMethod, updatePath, updateKey, isExternalTagging } =
                TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

    const handleChange = async (newValue: string, option: { value: string, label: string }) => {
        if (selectedValue !== newValue) {
            const tags = newValue ? [+newValue] : [+avlTagsData.filter(tag => tag.tagName === selectedValue)[0].tagId];
            setLoading(true);
            await addEntityTags(
                [{sku: params.data.sku, channel: params.data.channel_id}], // skuId params
                updateMethod,
                updatePath,
                updateKey,
                tags,
                false, // isBulkRequest
                newValue ? TaggingActionTypes.INSERT : TaggingActionTypes.UPDATE //action
            );

            params.node.setDataValue(params.colDef.field, option.label);
            setSelectedValue(tags[0].toString());
            setLoading(false);
        }
        params.api.stopEditing();
    };

    return (
        <div
            ref={wrapperRef}
            style={{ width: "100%", height: "100%", padding: "4px" }}
        >
            <Spin spinning={loading}>
                <Select
                    value={selectedValue}
                    onChange={(value, option) => {
                        handleChange(value, option as { value: string; label: string });
                    }}
                    options={options}
                    style={{ width: "100%" }}
                    showSearch
                    allowClear
                    autoFocus
                    open={open}
                    onDropdownVisibleChange={setOpen}
                    variant={"borderless"}
                    placeholder={`Select ${params.colDef.field}`}
                    filterOption={(input, option) =>
                        (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </Spin>
        </div>
    );
};

export default VcpmGlobalTagCellEditor;
